import React, { useEffect, useState, useRef } from "react";
import "./Alert.css";
import SubTitle from "../../components/common/SubTitle";
import DateRangeSelector from "./DateRangeSelector";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import { formatDate } from "../../utils/formatDate";
import ExpiryIcon from "../../images/alerts/Expiry.svg";
import UsersIcon from "../../images/alerts/Users.svg";
import VehicleIcon from "../../images/alerts/Vehicle.svg";
import {
  Box,
  Typography,
  Card,
  Tabs,
  Tab,
  TextField,
  Switch,
  Checkbox,
  InputAdornment,
  IconButton,
  Button,
  Paper,
  Stack,
  useMediaQuery,
  useTheme,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  getAlertCounts,
  getAllAlerts,
  getUserAlerts,
  getVehicleAlerts,
  getExpiryAlerts,
  deleteAlertById,
  updateAlert,
} from "./AlertsApis";
import { useTranslation } from "react-i18next";
import MarkAsRead from "../../images/markasRead.svg"
import DeleteAlert from "../../images/deleteAlert.svg"

const ALERT_PAGE_LIMIT = 100;
const AUTO_FETCH_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds
const DEFAULT_ALERT_COUNTS = {
  total: 0,
  byType: { USER: 0, VEHICLE: 0, EXPIRY: 0 },
};
const ALERT_TAB_KEYS = ["all", "vehicle", "user", "expiry"];
const ALERT_TAB_FETCHERS = {
  all: getAllAlerts,
  vehicle: getVehicleAlerts,
  user: getUserAlerts,
  expiry: getExpiryAlerts,
};

const getDefaultAlertsByTab = () => ({
  all: [],
  vehicle: [],
  user: [],
  expiry: [],
});

const getDefaultPaginationByTab = () => ({
  all: { hasMore: false, nextCursor: null },
  vehicle: { hasMore: false, nextCursor: null },
  user: { hasMore: false, nextCursor: null },
  expiry: { hasMore: false, nextCursor: null },
});

const normalizeAlert = (alert) => ({
  ...alert,
  isRead: alert.statusFlag === "READ",
});

const mergeAlertsById = (currentAlerts, nextAlerts) => {
  const alertMap = new Map(
    (Array.isArray(currentAlerts) ? currentAlerts : []).map((alert) => [
      alert.id,
      alert,
    ]),
  );

  (Array.isArray(nextAlerts) ? nextAlerts : []).forEach((alert) => {
    alertMap.set(alert.id, {
      ...(alertMap.get(alert.id) || {}),
      ...alert,
    });
  });

  return Array.from(alertMap.values()).sort(
    (firstAlert, secondAlert) =>
      new Date(secondAlert.createdAt) - new Date(firstAlert.createdAt),
  );
};

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  "& .MuiInputLabel-outlined": {
    transform: "translate(16px, 8px) scale(1)",
  },
  "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
    transform: "translate(14px, -6px) scale(0.75)",
    backgroundColor: "#F2F2F3",
    padding: "0 4px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "none",
    border: "none"
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "none",
    border: "none"
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "none",
    border: "none"
  },
}));


const Alert = () => {
  const { t } = useTranslation();
  const [alertsByTab, setAlertsByTab] = useState(getDefaultAlertsByTab);
  const [paginationByTab, setPaginationByTab] = useState(
    getDefaultPaginationByTab,
  );
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isUnreadOnly, setIsUnreadOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc");
  const [expandedAlerts, setExpandedAlerts] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [alertCounts, setAlertCounts] = useState(DEFAULT_ALERT_COUNTS);
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isMultiDeleteModalOpen, setIsMultiDeleteModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const observerRef = useRef(null);
  const lastAlertRef = useRef(null);
  const activeTabKeyRef = useRef(ALERT_TAB_KEYS[0]);
  const activeTabKey = ALERT_TAB_KEYS[selectedTab] || "all";
  const vehicleAlerts = alertsByTab.vehicle;
  const activePagination = paginationByTab[activeTabKey] || {
    hasMore: false,
    nextCursor: null,
  };

  useEffect(() => {
    activeTabKeyRef.current = activeTabKey;
  }, [activeTabKey]);

  const updateAllLoadedTabs = (updater) => {
    setAlertsByTab((prevAlertsByTab) => {
      const nextAlertsByTab = {};

      Object.keys(prevAlertsByTab).forEach((tabKey) => {
        nextAlertsByTab[tabKey] = updater(prevAlertsByTab[tabKey], tabKey);
      });

      return nextAlertsByTab;
    });
  };

  const findLoadedAlertById = (alertId) =>
    Object.values(alertsByTab)
      .flat()
      .find((alert) => alert.id === alertId);

  const fetchAlertCountsData = async () => {
    try {
      const counts = await getAlertCounts();
      setAlertCounts(counts || DEFAULT_ALERT_COUNTS);
    } catch (fetchError) {
      console.error("Error fetching alert counts:", fetchError);
      setAlertCounts(DEFAULT_ALERT_COUNTS);
    }
  };

  const fetchAlertsForTab = async (tabKey, { reset = false } = {}) => {
    const fetchAlertsByTab = ALERT_TAB_FETCHERS[tabKey];

    if (!fetchAlertsByTab) {
      return;
    }

    if (reset) {
      setIsLoading(true);
    } else {
      setIsFetchingMore(true);
    }

    if (activeTabKeyRef.current === tabKey) {
      setError(null);
    }

    try {
      const response = await fetchAlertsByTab(
        reset ? null : paginationByTab[tabKey]?.nextCursor,
        ALERT_PAGE_LIMIT,
      );
      const normalizedAlerts = response.alerts.map(normalizeAlert);

      setAlertsByTab((prevAlertsByTab) => ({
        ...prevAlertsByTab,
        [tabKey]: reset
          ? normalizedAlerts
          : mergeAlertsById(prevAlertsByTab[tabKey], normalizedAlerts),
      }));
      setPaginationByTab((prevPaginationByTab) => ({
        ...prevPaginationByTab,
        [tabKey]: {
          hasMore: response.hasMore,
          nextCursor: response.nextCursor,
        },
      }));
    } catch (fetchError) {
      console.error(`Error fetching ${tabKey} alerts:`, fetchError);

      if (activeTabKeyRef.current === tabKey) {
        setError("Failed to load alerts. Please try again later.");
      }
    } finally {
      if (activeTabKeyRef.current === tabKey) {
        if (reset) {
          setIsLoading(false);
        } else {
          setIsFetchingMore(false);
        }
      }
    }
  };

  useEffect(() => {
    setSelectedAlerts([]);
    setSelectAll(false);
    setExpandedAlerts({});
    setSelectedVehicle("");
    setSelectedStatus("");
    setIsFetchingMore(false);
    setAlertsByTab((prevAlertsByTab) => ({
      ...prevAlertsByTab,
      [activeTabKey]: [],
    }));
    setPaginationByTab((prevPaginationByTab) => ({
      ...prevPaginationByTab,
      [activeTabKey]: {
        hasMore: false,
        nextCursor: null,
      },
    }));

    fetchAlertCountsData();
    fetchAlertsForTab(activeTabKey, { reset: true });

    const intervalId = setInterval(() => {
      fetchAlertCountsData();
      fetchAlertsForTab(activeTabKeyRef.current, { reset: true });
    }, AUTO_FETCH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [activeTabKey]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const getVisibleAlerts = () => {
    let alerts = [...(alertsByTab[activeTabKey] || [])];

    // Apply unread filter
    if (isUnreadOnly) {
      alerts = alerts.filter((alert) => !alert.isRead);
    }
    // Apply search filter
    if (searchTerm) {
      alerts = alerts.filter(
        (alert) =>
          alert.subject &&
          alert.subject.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    // Apply date range filter
    if (startDate && endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      alerts = alerts.filter((alert) => {
        const alertDate = new Date(alert.createdAt);
        return alertDate >= startDate && alertDate <= endOfDay;
      });
    }

    if (selectedTab === 1 && selectedVehicle) {
      alerts = alerts.filter((alert) => alert.vehicleId === selectedVehicle);
    }

    if (selectedTab === 1 && selectedStatus) {
      alerts = alerts.filter((alert) => alert.status === selectedStatus);
    }

    if (selectedTab === 3 && selectedVehicle) {
      const selectedExpiryType = selectedVehicle.toLowerCase();
      alerts = alerts.filter((alert) =>
        [alert.subject, alert.body, alert.entityType, alert.entityId]
          .filter(Boolean)
          .some((value) =>
            String(value).toLowerCase().includes(selectedExpiryType),
          ),
      );
    }

    // Sort by date
    alerts.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortDirection === "desc" ? dateB - dateA : dateA - dateB;
    });
    return alerts;
  };

  const visibleAlerts = getVisibleAlerts();

  useEffect(() => {
    if (visibleAlerts.length === 0) {
      setSelectAll(false);
      return;
    }

    const areAllVisibleAlertsSelected = visibleAlerts.every((alert) =>
      selectedAlerts.includes(alert.id),
    );

    setSelectAll(areAllVisibleAlertsSelected);
  }, [selectedAlerts, visibleAlerts]);

  useEffect(() => {
    if (
      !lastAlertRef.current ||
      isLoading ||
      isFetchingMore ||
      !activePagination.hasMore ||
      !activePagination.nextCursor
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchAlertsForTab(activeTabKeyRef.current);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(lastAlertRef.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    activePagination.hasMore,
    activePagination.nextCursor,
    activeTabKey,
    isFetchingMore,
    isLoading,
    visibleAlerts.length,
  ]);

  const getAlertIcon = (alert) => {
    if (alert.type === "VEHICLE") {
      return <img src={VehicleIcon} alt="Vehicle" />;
    } else if (alert.type === "USER") {
      return <img src={UsersIcon} alt="User" />;
    } else if (alert.type === "EXPIRY") {
      return <img src={ExpiryIcon} alt="Expiry" />;
    } else {
      return <NotificationsIcon color="primary" />;
    }
  };

  const getAlertExcerpt = (alert) => {
    if (!alert.body) return "No additional details";

    const singleLineBody = alert.body.replace(/\n/g, " ");
    return singleLineBody.length > 50
      ? `${singleLineBody.substring(0, 50)}...`
      : singleLineBody;
  };


  const getAllCount = () => alertCounts.total || 0;
  const getVehicleCount = () => alertCounts.byType?.VEHICLE || 0;
  const getUserCount = () => alertCounts.byType?.USER || 0;
  const getExpiryCount = () => alertCounts.byType?.EXPIRY || 0;

  const toggleExpandAlert = (alertId, event) => {
    event.stopPropagation();
    setExpandedAlerts((prev) => ({
      ...prev,
      [alertId]: !prev[alertId],
    }));

    const alert = findLoadedAlertById(alertId);
    if (alert && !alert.isRead) {
      markAlertAsRead(alertId);
    }
  };

  const openAlert = (alert, event) => {
    toggleExpandAlert(alert.id, event);
  };

  const handleSelectAlert = (alertId, event) => {
    event.stopPropagation();
    setSelectedAlerts((prev) => {
      if (prev.includes(alertId)) {
        return prev.filter((id) => id !== alertId);
      } else {
        return [...prev, alertId];
      }
    });
  };

  const handleSelectAll = (event) => {
    event.stopPropagation();
    const checked = event.target.checked;
    setSelectAll(checked);
    if (checked) {
      setSelectedAlerts(visibleAlerts.map((alert) => alert.id));
    } else {
      setSelectedAlerts([]);
    }
  };

  const deleteAlert = async (alertId, { refreshCounts = true } = {}) => {
    try {
      await deleteAlertById(alertId);
      updateAllLoadedTabs((alerts) => alerts.filter((alert) => alert.id !== alertId));
      setSelectedAlerts((prevSelectedAlerts) =>
        prevSelectedAlerts.filter((selectedAlertId) => selectedAlertId !== alertId),
      );
      setExpandedAlerts((prevExpandedAlerts) => {
        const nextExpandedAlerts = { ...prevExpandedAlerts };
        delete nextExpandedAlerts[alertId];
        return nextExpandedAlerts;
      });

      if (refreshCounts) {
        await fetchAlertCountsData();
      }

      return true;
    } catch (err) {
      console.error("Error deleting alert:", err);
      return false;
    }
  };

  const deleteMultipleAlerts = async () => {
    setIsLoading(true);
    let success = true;
    for (const alertId of selectedAlerts) {
      const result = await deleteAlert(alertId, { refreshCounts: false });
      if (!result) {
        success = false;
      }
    }
    await fetchAlertCountsData();
    setIsLoading(false);
    setIsMultiDeleteModalOpen(false);
    setSelectedAlerts([]);
    setSelectAll(false);
    if (!success) {
      setError("Some alerts could not be deleted. Please try again.");
    }
  };

  const markAlertAsRead = async (alertId) => {
    try {
      const alertToUpdate = findLoadedAlertById(alertId);
      if (alertToUpdate) {
        const updatedData = {
          ...alertToUpdate,
          alertStatus: "read",
          statusFlag: "READ",
        };
        await updateAlert(updatedData);
        updateAllLoadedTabs((alerts) =>
          alerts.map((alert) =>
            alert.id === alertId
              ? {
                  ...alert,
                  isRead: true,
                  alertStatus: "read",
                  statusFlag: "READ",
                }
              : alert,
          ),
        );
      }
    } catch (err) {
      console.error("Error marking alert as read:", err);
      setError("Failed to update alert status. Please try again.");
    }
  };

  const markAlertAsUnread = async (alertId) => {
    try {
      const alertToUpdate = findLoadedAlertById(alertId);
      if (alertToUpdate) {
        const updatedData = {
          ...alertToUpdate,
          alertStatus: "unRead",
          statusFlag: "UNREAD",
        };
        await updateAlert(updatedData);
        updateAllLoadedTabs((alerts) =>
          alerts.map((alert) =>
            alert.id === alertId
              ? {
                  ...alert,
                  isRead: false,
                  alertStatus: "unRead",
                  statusFlag: "UNREAD",
                }
              : alert,
          ),
        );
      }
    } catch (err) {
      console.error("Error marking alert as unread:", err);
      setError("Failed to update alert status. Please try again.");
    }
  };

  const markMultipleAsRead = async () => {
    try {
      setIsLoading(true);
      for (const alertId of selectedAlerts) {
        const alertToUpdate = findLoadedAlertById(alertId);
        if (alertToUpdate && !alertToUpdate.isRead) {
          const updatedData = {
            ...alertToUpdate,
            alertStatus: "read",
            statusFlag: "READ",
          };
          await updateAlert(updatedData);
        }
      }
      updateAllLoadedTabs((alerts) =>
        alerts.map((alert) =>
          selectedAlerts.includes(alert.id)
            ? {
                ...alert,
                isRead: true,
                alertStatus: "read",
                statusFlag: "READ",
              }
            : alert,
        ),
      );
      setSelectedAlerts([]);
      setSelectAll(false);
    } catch (err) {
      console.error("Error marking multiple alerts as read:", err);
      setError("Failed to update alert statuses. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const markMultipleAsUnread = async () => {
    try {
      setIsLoading(true);
      for (const alertId of selectedAlerts) {
        const alertToUpdate = findLoadedAlertById(alertId);
        if (alertToUpdate && alertToUpdate.isRead) {
          const updatedData = {
            ...alertToUpdate,
            alertStatus: "unRead",
            statusFlag: "UNREAD",
          };
          await updateAlert(updatedData);
        }
      }
      updateAllLoadedTabs((alerts) =>
        alerts.map((alert) =>
          selectedAlerts.includes(alert.id)
            ? {
                ...alert,
                isRead: false,
                alertStatus: "unRead",
                statusFlag: "UNREAD",
              }
            : alert,
        ),
      );
      setSelectedAlerts([]);
      setSelectAll(false);
    } catch (err) {
      console.error("Error marking multiple alerts as unread:", err);
      setError("Failed to update alert statuses. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const hasReadAlerts = selectedAlerts.some((alertId) => {
    const alert = findLoadedAlertById(alertId);
    return alert && alert.isRead;
  });

  const hasUnreadAlerts = selectedAlerts.some((alertId) => {
    const alert = findLoadedAlertById(alertId);
    return alert && !alert.isRead;
  });
  const orgName = JSON.parse(localStorage.getItem('userState'))?.organization?.name;
  return (
    <>
      <Box sx={{ backgroundColor: "#f5f7f9", minHeight: "100vh", minWidth: "90vw" }}>
        <SubTitle title="Alerts" path={`${orgName || "organization"} >  Alerts`} />
        <Box sx={{ margin: "0px 40px" }}>
          <Paper elevation={4} sx={{ p: 2, borderRadius: 2, border: "1px solid #A5DCF3", position: "relative" }}>
            <Box sx={{ padding: "20px" }}>
              {error && (
                <Box sx={{ backgroundColor: "#f8d7da", color: "#721c24", padding: "10px", marginBottom: "15px", borderRadius: "5px" }}>
                  {error}
                </Box>
              )}
              <Card variant="none" sx={{ borderRadius: "8px", mb: 2, padding: "2px" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isSmallScreen ? "column" : "row",
                    justifyContent: "space-between",
                    alignItems: isSmallScreen ? "stretch" : "center",
                    borderRadius: "8px",
                  }}
                >
                  <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    variant={isXsScreen ? "scrollable" : "standard"}
                    scrollButtons={isXsScreen ? "auto" : false}
                    allowScrollButtonsMobile={isXsScreen}
                    sx={{
                      background: "#E8EAEE",
                      boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                      borderRadius: "8px",
                      width: isSmallScreen ? "100%" : "65%",
                      display: "flex",
                      alignItems: "center",
                      padding: "2px",
                      justifyContent: "space-evenly",
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontWeight: 600,
                        minWidth: isXsScreen ? 80 : 100,
                        flex: isXsScreen ? "none" : 1,
                        padding: isXsScreen ? "6px 8px" : "12px 16px",
                        fontSize: isXsScreen ? "0.75rem" : "inherit",
                      },
                      "& .Mui-selected": {
                        backgroundColor: "white",
                        boxShadow: "rgba(0, 0, 0, 0.24) 4px 3px 8px",
                        zIndex: 2,
                        position: "relative",
                        border: "0.5px solid grey",
                        borderRadius: "8px 8px 0px 0px",
                      },
                      "& .MuiTabs-indicator": {
                        backgroundColor: "#20A8E0",
                        height: "3px",
                        zIndex: "2",
                      },
                    }}
                  >
                    <Tab
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: isXsScreen ? "wrap" : "nowrap" }}>
                          <Typography className="tab-text" sx={{ fontSize: isXsScreen ? "0.75rem" : "inherit" }}>{t("all")}</Typography>
                          <Box sx={{ ml: isXsScreen ? 0.5 : 1, backgroundColor: "#B7BFCC", borderRadius: "8px", px: isXsScreen ? 0.8 : 1.5, py: 0.2 }}>
                            <Typography variant="body2" sx={{ fontSize: isXsScreen ? "0.65rem" : "inherit" }}>{getAllCount()}</Typography>
                          </Box>
                        </Box>
                      }
                    />
                    <Tab
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: isXsScreen ? "wrap" : "nowrap" }}>
                          <Typography className="tab-text" sx={{ fontSize: isXsScreen ? "0.75rem" : "inherit" }}>{t("Vehicles")}</Typography>
                          <Box sx={{ ml: isXsScreen ? 0.5 : 1, backgroundColor: "#B7BFCC", borderRadius: "8px", px: isXsScreen ? 0.8 : 1.5, py: 0.2 }}>
                            <Typography variant="body2" sx={{ fontSize: isXsScreen ? "0.65rem" : "inherit" }}>{getVehicleCount()}</Typography>
                          </Box>
                        </Box>
                      }
                    />
                    <Tab
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: isXsScreen ? "wrap" : "nowrap" }}>
                          <Typography className="tab-text" sx={{ fontSize: isXsScreen ? "0.75rem" : "inherit" }}>{t("users")}</Typography>
                          <Box sx={{ ml: isXsScreen ? 0.5 : 1, backgroundColor: "#B7BFCC", borderRadius: "8px", px: isXsScreen ? 0.8 : 1.5, py: 0.2 }}>
                            <Typography variant="body2" sx={{ fontSize: isXsScreen ? "0.65rem" : "inherit" }}>{getUserCount()}</Typography>
                          </Box>
                        </Box>
                      }
                    />
                    <Tab
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: isXsScreen ? "wrap" : "nowrap" }}>
                          <Typography className="tab-text" sx={{ fontSize: isXsScreen ? "0.75rem" : "inherit" }}>{t("expiry")}</Typography>
                          <Box sx={{ ml: isXsScreen ? 0.5 : 1, backgroundColor: "#B7BFCC", borderRadius: "8px", px: isXsScreen ? 0.8 : 1.5, py: 0.2 }}>
                            <Typography variant="body2" sx={{ fontSize: isXsScreen ? "0.65rem" : "inherit" }}>{getExpiryCount()}</Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </Tabs>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: isSmallScreen ? "100%" : "auto",
                      justifyContent: isSmallScreen ? "space-between" : "flex-end",
                      mt: isSmallScreen ? 1 : 0,
                    }}
                  >
                    <TextField
                      placeholder={t("search")}
                      variant="outlined"
                      size="small"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ marginRight: "8px" }}>
                            <IconButton edge="end" sx={{ padding: "4px" }}>
                              <SearchIcon sx={{ fontSize: "20px", color: "#1F242E" }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: isXsScreen ? "60%" : 200,
                        minWidth: isXsScreen ? "150px" : "200px",
                        "& .MuiInputBase-root": {
                          height: "35px",
                          border: "1px solid #414958",
                          borderRadius: "4px",
                          background: "#FFFFFF",
                          display: "flex",
                          alignItems: "center",
                          padding: "0 14px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "& .MuiInputBase-input": {
                          color: "#1F242E",
                          padding: "8px 0",
                          lineHeight: "1.5",
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: "#1F242E",
                          opacity: 1,
                          fontSize: "14px",
                          lineHeight: "35px",
                        },
                      }}
                    />

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '35px', // Match TextField height exactly
                        gap: 0.5,
                        flexShrink: 0, // Prevent shrinking
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#183059",
                          fontSize: isXsScreen ? "0.75rem" : "14px",
                          fontWeight: 400,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t("unread")}
                      </Typography>
                      <Switch
                        checked={isUnreadOnly}
                        onChange={(e) => setIsUnreadOnly(e.target.checked)}
                        size={isXsScreen ? "small" : "medium"}
                        sx={{
                          width: 42,
                          height: 26,
                          padding: 0,
                          margin: 0,
                          '& .MuiSwitch-switchBase': {
                            padding: 0,
                            margin: 2,
                            transitionDuration: '300ms',
                            '&.Mui-checked': {
                              transform: 'translateX(16px)',
                              color: '#fff',
                              '& + .MuiSwitch-track': {
                                backgroundColor: '#20A8E0',
                                opacity: 1,
                                border: 0,
                              },
                            },
                          },
                          '& .MuiSwitch-thumb': {
                            boxSizing: 'border-box',
                            width: 22,
                            height: 22,
                            transform: 'translate(-14px, -14px)', 
                          },
                          '& .MuiSwitch-track': {
                            borderRadius: 13,
                            backgroundColor: '#E9E9EA',
                            opacity: 1,
                            transition: 'background-color 300ms',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Card>
              <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isSmallScreen ? "column" : "row",
                    gap: isSmallScreen ? "16px" : "20px",
                    alignItems: isSmallScreen ? "stretch" : "center",
                    mb: 2,
                  }}
                >
                  <DateRangeSelector
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={setStartDate}
                    onEndDateChange={setEndDate}
                  />
                  {selectedTab === 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: isSmallScreen ? "column" : "row",
                        gap: "12px",
                        width: isSmallScreen ? "100%" : "auto",
                      }}
                    >
                      <StyledFormControl sx={{ minWidth: isSmallScreen ? "100%" : "202px" }}>
                        <InputLabel id="vehicle-filter-label">{t("Vehicle")}</InputLabel>
                        <StyledSelect
                          labelId="vehicle-filter-label"
                          id="vehicle-filter-select"
                          value={selectedVehicle}
                          label={t("Vehicle")}
                          onChange={(e) => setSelectedVehicle(e.target.value)}
                          fullWidth
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: "200px",
                                "& .MuiMenuItem-root": {
                                  height: "35px",
                                  padding: "8px 16px",
                                  display: "flex",
                                  alignItems: "center",
                                },
                              },
                            },
                          }}
                          sx={{
                            height: "35px",
                            borderRadius: "4px",
                            bgcolor: "#F2F2F3",
                            border: "1px solid #414958",
                          }}
                        >
                          <MenuItem value="">
                            <em>{t("All Vehicles")}</em>
                          </MenuItem>
                          {[...new Set(vehicleAlerts.map((alert) => alert.vehicleId))]
                            .filter(Boolean)
                            .map((vehicleId) => (
                              <MenuItem key={vehicleId} value={vehicleId}>
                                {vehicleId}
                              </MenuItem>
                            ))}
                        </StyledSelect>
                      </StyledFormControl>
                      <StyledFormControl sx={{ minWidth: isSmallScreen ? "100%" : "202px" }}>
                        <InputLabel id="status-filter-label">{t("Status")}</InputLabel>
                        <StyledSelect
                          labelId="status-filter-label"
                          id="status-filter-select"
                          value={selectedStatus}
                          label={t("Status")}
                          onChange={(e) => setSelectedStatus(e.target.value)}
                          fullWidth
                          sx={{
                            height: "35px",
                            borderRadius: "4px",
                            bgcolor: "#F2F2F3",
                            border: "1px solid #414958",
                          }}
                        >
                          <MenuItem value="">
                            <em>{t("All Status")}</em>
                          </MenuItem>
                          <MenuItem value="active">{t("Active")}</MenuItem>
                          <MenuItem value="inactive">{t("Inactive")}</MenuItem>
                          <MenuItem value="dead">{t("Dead")}</MenuItem>
                        </StyledSelect>
                      </StyledFormControl>
                    </Box>
                  )}
                  {selectedTab === 3 && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: isSmallScreen ? "column" : "row",
                        gap: "12px",
                        width: isSmallScreen ? "100%" : "auto",
                      }}
                    >
                      <StyledFormControl sx={{ minWidth: isSmallScreen ? "100%" : "202px" }}>
                        <InputLabel id="expiry-type-filter-label">{t("Expiry Type")}</InputLabel>
                        <StyledSelect
                          labelId="expiry-type-filter-label"
                          id="expiry-type-filter-select"
                          value={selectedVehicle}
                          label={t("Expiry Type")}
                          onChange={(e) => setSelectedVehicle(e.target.value)}
                          fullWidth
                          sx={{
                            height: "35px",
                            borderRadius: "4px",
                            bgcolor: "#F2F2F3",
                            border: "1px solid #414958",
                          }}
                        >
                          <MenuItem value="">
                            <em>{t("All Expiry Type")}</em>
                          </MenuItem>
                          <MenuItem value="serviceDue">{t("Service Due Date")}</MenuItem>
                          <MenuItem value="insuranceRenewal">{t("Insurance Renewal Due Date")}</MenuItem>
                          <MenuItem value="reRegistration">{t("Re-Registration Due Date")}</MenuItem>
                          <MenuItem value="emissionTesting">{t("Emission Testing Due Date")}</MenuItem>
                        </StyledSelect>
                      </StyledFormControl>
                    </Box>
                  )}
                </Box>
                {selectedAlerts.length > 0 && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      startIcon={<img src={DeleteAlert} alt="delete" />}
                      onClick={() => setIsMultiDeleteModalOpen(true)}
                      sx={{ borderRadius: "4px", textTransform: "none" }}
                    >
                      ({selectedAlerts.length})
                    </Button>
                    {hasUnreadAlerts && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<img src={MarkAsRead} alt="Mark as Read" />}
                        onClick={markMultipleAsRead}
                        sx={{
                          bgcolor: "#F9E719",
                          borderRadius: "4px",
                          textTransform: "none",
                          border: " 1px solid #414958",

                        }}
                      >
                        {t("Mark as read")}
                      </Button>
                    )}
                    {hasReadAlerts && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityOffIcon />}
                        onClick={markMultipleAsUnread}
                        sx={{
                          bgcolor: "#F9E719",
                          borderRadius: "4px",
                          textTransform: "none",
                          border: " 1px solid #414958",

                        }}                      >
                        {t("Mark as unread")}
                      </Button>
                    )}

                  </Box>
                )}
              </Box>
              <Card variant="outlined" sx={{ borderRadius: "8px" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 16px",
                    borderBottom: "1px solid #B7BFCC",
                    backgroundColor: "#647490",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      size="small"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      sx={{ pl: 0, color: "white", "&.Mui-checked": { color: "white" } }}
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#FFFFFF" }}>
                      {t("Alerts")}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#FFFFFF" }}>
                      {t("timestamp")}
                    </Typography>
                    <IconButton
                      sx={{ color: "#20A8E0" }}
                      size="small"
                      onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
                    >
                      <ArrowDropDownIcon
                        sx={{ size: 30, transform: sortDirection === "asc" ? "rotate(180deg)" : "rotate(0deg)" }}
                      />
                    </IconButton>
                  </Box>
                </Box>
                {isLoading && visibleAlerts.length === 0 ? (
                  <Box sx={{ textAlign: "center", padding: "20px" }}>
                    <Typography>{t("Loading Alerts")}...</Typography>
                  </Box>
                ) : visibleAlerts.length === 0 ? (
                  <Box sx={{ textAlign: "center", padding: "20px" }}>
                    <Typography>{t("No Alerts Found")}</Typography>
                  </Box>
                ) : (
                  visibleAlerts.map((alert, index) => (
                    <Box key={alert.id} ref={index === visibleAlerts.length - 1 ? lastAlertRef : null}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "12px 16px",
                          borderBottom: expandedAlerts[alert.id] ? "none" : "1px solid #B7BFCC",
                          backgroundColor: alert.isRead ? "#ffffff" : "#f8fafd",
                          "&:hover": { backgroundColor: "#f5f5f5" },
                          cursor: "pointer",
                        }}
                        onClick={(e) => openAlert(alert, e)}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                          <Checkbox
                            size="small"
                            sx={{ mr: 1 }}
                            checked={selectedAlerts.includes(alert.id)}
                            onChange={(e) => handleSelectAlert(alert.id, e)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Box sx={{ mr: 2, display: "flex", alignItems: "center" }}>
                            {getAlertIcon(alert)}
                          </Box>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: alert.isRead ? 400 : 600, mb: 0.5 }}>
                              {alert.subject}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ whiteSpace: "pre-line" }}>
                              {getAlertExcerpt(alert)}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="body2" color="textSecondary">
                            {formatDate(alert.createdAt)}
                          </Typography>
                          <IconButton size="small" sx={{ ml: 1 }} onClick={(e) => toggleExpandAlert(alert.id, e)}>
                            <ExpandMoreIcon
                              sx={{ transform: expandedAlerts[alert.id] ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s" }}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                      {expandedAlerts[alert.id] && (
                        <Box
                          sx={{
                            padding: "16px 40px",
                            backgroundColor: "#f8fafd",
                            borderBottom: "1px solid #B7BFCC",
                            position: "relative",
                          }}
                        >
                          <Stack spacing={2}>
                            <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>{alert.body}</Typography>
                            <Stack direction="row" spacing={2}>
                              <Box flex={1}>
                                <Typography variant="body2" color="textSecondary">
                                  <strong>Date:</strong> {new Date(alert.createdAt).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Box flex={1}>
                                <Typography variant="body2" color="textSecondary">
                                  <strong>Time:</strong> {new Date(alert.createdAt).toLocaleTimeString()}
                                </Typography>
                                {alert.userName && (
                                  <Typography variant="body2" color="textSecondary">
                                    <strong>User:</strong> {alert.userName}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  ))
                )}
                {isFetchingMore && (
                  <Box sx={{ textAlign: "center", padding: "20px" }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Card>
            </Box>
          </Paper>
        </Box>
        <DeleteConfirmationDialog
          open={isMultiDeleteModalOpen}
          onClose={() => setIsMultiDeleteModalOpen(false)}
          onConfirm={deleteMultipleAlerts}
          isLoading={isLoading}
          isMulti
          count={selectedAlerts.length}
        />
      </Box>
    </>
  );
};

export default Alert;
